import { useState, useEffect, useCallback, useRef, KeyboardEvent } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import {
	addDsTab,
	removeDsTab,
	removeDsPick,
	setActiveDsTab,
	toggleDsPick,
} from 'redux/mode/actions';
import { displayConfirmModal } from 'redux/mode/util';
import useScrollMemory from 'hooks/useScrollMemory';
import forge from 'apis/forge';
import { titleToPath } from 'designSystem/content/util';
import MarkdownEditor from 'components/Widget/Input/MarkdownEditor';
import { designSystemEditingBlocked } from 'redux/db/util';
import * as Base from '../styles';
import * as GalleryS from '../GalleryView/styles';
import * as S from './styles';

const SAVE_DEBOUNCE_MS = 1500;

const GuidelinesView = () => {
	const dispatch = useDispatch();
	const [selectedGuidelineIndex, setSelectedGuidelineIndex] = useState<number | null>(null);
	const [localTitle, setLocalTitle] = useState('');
	const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingSaveRef = useRef<(() => void) | null>(null);
	const titleInputRef = useRef<HTMLInputElement>(null);
	const { designSystemContent, activeTab, selectedDsPicks, activeDesignSystemId } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeTab: selectActiveDsTab(r.mode),
			selectedDsPicks: r.mode.selectedDsPicks,
			activeDesignSystemId: r.mode.activeDesignSystemId,
		}),
		shallowEqual
	);
	const guidelines = designSystemContent?.guidelines || [];

	const contentPath = activeTab?.contentPath;
	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	useEffect(() => {
		if (contentPath) {
			// Specific guideline requested — only select it if found, don't fall back to first
			const index = guidelines.findIndex((g) => g.path === contentPath);
			setSelectedGuidelineIndex(index >= 0 ? index : null);
		} else {
			setSelectedGuidelineIndex(guidelines.length > 0 ? 0 : null);
		}
	}, [guidelines, contentPath]);

	const selectedGuideline =
		selectedGuidelineIndex !== null ? guidelines[selectedGuidelineIndex] : null;

	// Sync local title from selected guideline
	useEffect(() => {
		setLocalTitle(selectedGuideline?.title ?? '');
	}, [selectedGuideline?.path]);

	// Flush any pending save on unmount
	useEffect(() => {
		return () => {
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
			pendingSaveRef.current?.();
		};
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onGuidelineChange = useCallback(
		(markdown: string) => {
			if (!activeDesignSystemId || !selectedGuideline) return;
			if (markdown === selectedGuideline.content) return;
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
			const save = () => {
				pendingSaveRef.current = null;
				forge.saveDsFiles({
					designSystemId: activeDesignSystemId,
					files: [{ path: selectedGuideline.path, content: markdown }],
				});
			};
			pendingSaveRef.current = save;
			saveTimerRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
		},
		[activeDesignSystemId, selectedGuideline]
	);

	const onRenameCommit = useCallback(() => {
		if (!activeDesignSystemId || !selectedGuideline || !activeTab) return;
		const trimmed = localTitle.trim();
		if (!trimmed || trimmed === selectedGuideline.title) {
			setLocalTitle(selectedGuideline.title);
			return;
		}
		if (designSystemEditingBlocked()) {
			setLocalTitle(selectedGuideline.title);
			return;
		}

		if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
		saveTimerRef.current = null;
		pendingSaveRef.current = null;
		const newPath = titleToPath(trimmed, 'guidelines');
		if (newPath === selectedGuideline.path) return;
		if (guidelines.some((g) => g.path === newPath)) {
			setLocalTitle(selectedGuideline.title);
			return;
		}

		const oldPath = selectedGuideline.path;
		const oldTabId = activeTab.id;
		const newTabId = `guidelines-${newPath}`;
		const wasSelected = selectedDsPicks.some(
			(p) => p.kind === 'guideline' && p.guidelinePath === oldPath
		);

		forge.updateDsFiles({
			designSystemId: activeDesignSystemId,
			files: [
				{ path: newPath, content: selectedGuideline.content },
				{ path: oldPath, content: '' },
			],
		});

		dispatch(removeDsTab(oldTabId));
		dispatch(
			addDsTab({ id: newTabId, section: 'guidelines', contentPath: newPath, label: trimmed })
		);
		dispatch(setActiveDsTab(newTabId));
		if (wasSelected) {
			dispatch(removeDsPick({ kind: 'guideline', guidelinePath: oldPath }));
			dispatch(toggleDsPick({ kind: 'guideline', guidelinePath: newPath }));
		}
	}, [
		activeDesignSystemId,
		selectedGuideline,
		activeTab,
		localTitle,
		guidelines,
		selectedDsPicks,
		dispatch,
	]);

	const onTitleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.currentTarget.blur();
			} else if (e.key === 'Escape') {
				setLocalTitle(selectedGuideline?.title ?? '');
				const el = e.currentTarget;
				setTimeout(() => el.blur(), 0);
			}
		},
		[selectedGuideline?.title]
	);

	const onDelete = useCallback(() => {
		if (!activeDesignSystemId || !selectedGuideline || !activeTab) return;
		if (designSystemEditingBlocked()) return;

		const path = selectedGuideline.path;
		const title = selectedGuideline.title;
		const tabId = activeTab.id;

		displayConfirmModal({
			title: 'Delete guideline',
			description: `Are you sure you want to delete "${title}"?`,
			submitLabel: 'Delete',
			onCancel: () => {},
			onSubmit: async () => {
				if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
				saveTimerRef.current = null;
				pendingSaveRef.current = null;
				await forge.updateDsFiles({
					designSystemId: activeDesignSystemId,
					files: [{ path, content: '' }],
				});
				dispatch(removeDsTab(tabId));
				dispatch(removeDsPick({ kind: 'guideline', guidelinePath: path }));
			},
		});
	}, [activeDesignSystemId, selectedGuideline, activeTab, dispatch]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	if (guidelines.length === 0) {
		return (
			<Base.SectionBase>
				<Base.SectionHeader>
					<Base.SectionTitle>Guidelines</Base.SectionTitle>
				</Base.SectionHeader>
				<Base.SectionContent>
					<p>No guidelines available.</p>
				</Base.SectionContent>
			</Base.SectionBase>
		);
	}

	const isEditable = !designSystemEditingBlocked();
	const isGuidelineSelected =
		selectedGuideline &&
		selectedDsPicks.some(
			(p) => p.kind === 'guideline' && p.guidelinePath === selectedGuideline.path
		);

	return (
		<Base.SectionBase style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
			<GalleryS.GalleryContainer>
				<GalleryS.GalleryPreviewContainer>
					{selectedGuideline ? (
						<S.ScrollWrapper ref={scrollRef}>
							<S.ContentContainer>
								<S.GuidelineHeader>
									<S.GuidelineTitleInput
										ref={titleInputRef}
										value={localTitle}
										onChange={(e) => setLocalTitle(e.target.value)}
										onBlur={onRenameCommit}
										onKeyDown={onTitleKeyDown}
										readOnly={!isEditable}
									/>
									<Base.SelectToggle
										$selected={!!isGuidelineSelected}
										onClick={() =>
											dispatch(
												toggleDsPick({
													kind: 'guideline',
													guidelinePath: selectedGuideline.path,
												})
											)
										}
									>
										{isGuidelineSelected ? 'Added to Chat' : 'Add to Chat'}
									</Base.SelectToggle>
									{isEditable && <S.DeleteButton onClick={onDelete}>Delete</S.DeleteButton>}
								</S.GuidelineHeader>
								<MarkdownEditor
									key={selectedGuideline.path}
									content={selectedGuideline.content}
									editable={isEditable}
									onChange={onGuidelineChange}
								/>
							</S.ContentContainer>
						</S.ScrollWrapper>
					) : (
						<GalleryS.EmptyState>Select a guideline to view</GalleryS.EmptyState>
					)}
				</GalleryS.GalleryPreviewContainer>
			</GalleryS.GalleryContainer>
		</Base.SectionBase>
	);
};

export default GuidelinesView;
